import { upcomingBands } from '../config/siteConfig';

const MONTH_MAP = {
  'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
  'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
};

const DAY_MAP = {
  'sun': { full: 'Sunday', index: 0 },
  'mon': { full: 'Monday', index: 1 },
  'tue': { full: 'Tuesday', index: 2 },
  'wed': { full: 'Wednesday', index: 3 },
  'thu': { full: 'Thursday', index: 4 },
  'fri': { full: 'Friday', index: 5 },
  'sat': { full: 'Saturday', index: 6 }
};

/**
 * Parse a band date string like "Sat, Apr 4" into a real Date object.
 * Infers the year (current or next) based on whether the date has passed.
 */
export function parseBandDate(dateStr) {
  if (!dateStr) return null;

  // Format: "Sat, Apr 4"
  const parts = dateStr.split(',');
  if (parts.length < 2) return null;

  const monthDay = parts[1].trim().split(/\s+/);
  if (monthDay.length < 2) return null;

  const monthAbbr = monthDay[0].toLowerCase().slice(0, 3);
  const day = parseInt(monthDay[1], 10);
  const month = MONTH_MAP[monthAbbr];

  if (month === undefined || isNaN(day)) return null;

  const now = new Date();
  let year = now.getFullYear();

  // Build candidate date in current year
  let date = new Date(year, month, day);

  // Year-boundary handling: a date string like "Fri, Jan 2" listed in
  // late December refers to NEXT year. Only roll forward when the date is
  // ~11+ months in the past — i.e. the next-year reading lands within the
  // next few weeks. A date merely a few weeks old (a stale entry) must
  // stay in the current year so it's correctly treated as passed, not as
  // an event a year from now.
  const diffMs = date.getTime() - now.getTime();
  if (diffMs < -320 * 24 * 60 * 60 * 1000) {
    date = new Date(year + 1, month, day);
  }

  return date;
}

/**
 * Filter out bands whose dates have already passed.
 */
export function getFutureBands() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return upcomingBands.filter((band) => {
    const bandDate = parseBandDate(band.date);
    if (!bandDate) return true;
    return bandDate >= today;
  });
}

/**
 * Get the full day name for the next band (e.g., "Saturday").
 */
export function getNextBandDayName() {
  const nextBand = getFutureBands()[0];
  if (!nextBand || nextBand.band === 'TBD') return null;

  const dayAbbr = nextBand.date.split(',')[0].trim().toLowerCase().slice(0, 3);
  return DAY_MAP[dayAbbr]?.full || null;
}

/**
 * Get a contextual date label for the next band date.
 * Returns strings like:
 *   "This Saturday"
 *   "Next Saturday"
 *   "on 4/3"
 *   "Saturday nights" (fallback)
 */
export function getNextBandDateLabel() {
  const nextBand = getFutureBands()[0];
  if (!nextBand || nextBand.band === 'TBD') return 'Coming Soon';

  const bandDate = parseBandDate(nextBand.date);
  if (!bandDate) return 'Coming Soon';

  const dayName = getNextBandDayName();
  if (!dayName) return 'Coming Soon';

  const now = new Date();
  // Strip time for day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(bandDate.getFullYear(), bandDate.getMonth(), bandDate.getDate());

  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Tonight';
  } else if (diffDays === 1) {
    return 'Tomorrow Night';
  } else if (diffDays > 1 && diffDays <= 6) {
    return `This ${dayName}`;
  } else if (diffDays > 6 && diffDays <= 13) {
    return `Next ${dayName}`;
  } else {
    // Format as "on M/D"
    const m = bandDate.getMonth() + 1;
    const d = bandDate.getDate();
    return `on ${m}/${d}`;
  }
}
